import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { TableAction, TableColumn, TableRow } from '../../../shared/components/data-table/data-table.component';
import { UsersService } from '../users.service';
import { ToolbarFilter } from '../../../shared/components/page-toolbar/page-toolbar.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { RolesService } from '../../roles/roles.service';
import { ConfirmActionModalData } from '../../../shared/components/confirm-action-modal/confirm-action-modal.model';
import { AuthStateService } from '../../../core/authentication/auth-state.service';

@Component({
  selector: 'app-user-list',
  standalone: false,
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {
  private usersService = inject(UsersService);
  private rolesService = inject(RolesService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authState = inject(AuthStateService);
  private _subs: Subscription[] = [];

  // Per-button emitter
  readonly addUserAction = new EventEmitter<void>();

  get headerActions(): HeaderAction[] {
    const actions: HeaderAction[] = [];
    if (this.authState.hasPermission('users.create')) {
      actions.push({ label: '+ Add user', variant: 'primary', emitter: this.addUserAction });
    }
    return actions;
  }

  userColumns: TableColumn[] = [
    { key: 'name', label: 'Name', type: 'name', sortable: true },
    { key: 'email', label: 'Email', type: 'email', sortable: true },
    { key: 'phone', label: 'Phone', type: 'phone', sortable: true },
    { key: 'role', label: 'Role', type: 'badge', sortable: true },
    { key: 'status', label: 'Status', type: 'status', sortable: true },
    { key: 'actions', label: '', type: 'actions' },
  ];

  // ── Confirmation modal state ────────────────────────────────
  isModalOpen = false;
  modalData: ConfirmActionModalData | null = null;
  private _pendingActionRow: TableRow | null = null;

  userActions: TableAction[] = [
    {
      label: 'Edit details',
      icon: 'assets/icons/edit-icon.svg',
      action: (row) => {
        this.router.navigate([`${row.id}/edit`], {relativeTo: this.route})
      },
      visible: () => this.authState.hasPermission('users.update')
    },
    {
      label: 'Deactivate user',
      icon: 'assets/icons/password.svg',
      action: (row) => this.openDeactivateModal(row),
      visible: (row) => row['status'] === 'Active' && this.authState.hasPermission('users.suspend')
    },
    {
      label: 'Activate user',
      icon: 'assets/icons/password.svg',
      action: (row) => this.openActivateModal(row),
      visible: (row) => row['status'] === 'Inactive' && this.authState.hasPermission('users.suspend')
    }
  ];

  userRows: TableRow[] = [];
  totalResults = 0;
  currentPage = 1;
  itemsPerPage = 12;

  searchQuery = '';
  selectedRole = '';
  selectedStatus = '';
  isLoading = false;

  filters: ToolbarFilter[] = [
    { key: 'role', label: 'Role', options: [] },
    { key: 'status', label: 'Status', options: ['Active', 'Inactive'] },
  ];

  ngOnInit(): void {
    this._subs.push(
      this.addUserAction.subscribe(() =>
        this.router.navigate(['create'], { relativeTo: this.route })
      )
    );
    this.loadRoles();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }

  loadRoles(): void {
    this.rolesService.getRoles().subscribe({
      next: (response) => {
        const roles = response.data.map((r) => r.name);
        const roleFilter = this.filters.find((f) => f.key === 'role');
        if (roleFilter) {
          roleFilter.options = roles;
        }
      },
      error: (err) => console.error('Failed to load roles:', err),
    });
  }

  loadUsers(): void {
    this.isLoading = true;

    // Map status to lower case format backend expects ("active" or "inactive")
    let statusFilter: any = undefined;
    if (this.selectedStatus === 'Active') {
      statusFilter = 'active';
    } else if (this.selectedStatus === 'Inactive') {
      statusFilter = 'inactive';
    }

    this.usersService
      .findAll({
        page: this.currentPage,
        limit: this.itemsPerPage,
        search: this.searchQuery || undefined,
        role: this.selectedRole || undefined,
        status: statusFilter,
      })
      .subscribe({
        next: (response) => {
          this.userRows = response.data.map((user) => ({
            id: user.id,
            name: user.full_name,
            email: user.email || '',
            phone: user.phone_number || '',
            role: user.role?.name || 'No Role',
            status: user.active ? 'Active' : 'Inactive',
          }));
          this.totalResults = response.total;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load users:', err);
          this.isLoading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsers();
  }

  onItemsPerPageChange(items: number): void {
    this.itemsPerPage = items;
    this.currentPage = 1;
    this.loadUsers();
  }


  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadUsers();
  }

  onFilterChange(filter: ToolbarFilter): void {
    if (filter.key === 'role') {
      this.selectedRole = filter.value || '';
    } else if (filter.key === 'status') {
      this.selectedStatus = filter.value || '';
    }
    this.currentPage = 1;
    this.loadUsers();
  }

  // ── Deactivate confirmation modal ───────────────────────────
  openDeactivateModal(row: TableRow): void {
    this._pendingActionRow = row;
    const userName: string = row['name'] ?? 'this user';
    const firstName: string = userName.split(' ')[0];


    this.modalData = {
      icon: 'lock',
      title: `Deactivate ${userName}?`,
      description: `Access will be revoked immediately. Historical data is **never deleted** — ${firstName} can be reactivated anytime to restore access.`,
      effects: [
        { type: 'negative', text: 'Login disabled — active sessions terminated within 60s' },
        { type: 'negative', text: 'User will no longer appear as an active assignee' },
        { type: 'positive', text: 'All audit logs, history, and assignments are preserved' },
        { type: 'positive', text: 'Reactivation possible at any time from this list' },
      ],
      confirmation: {
        label: userName,
        expectedValue: userName,
        placeholder: `Type "${userName}" to confirm`,
      },
      actions: [
        {
          label: 'Deactivate',
          style: 'danger',
          action: 'deactivate',
          disabledUntilConfirmed: true,
        },
      ],
    };
    this.isModalOpen = true;
  }

  openActivateModal(row: TableRow): void {
    this._pendingActionRow = row;
    const userName: string = row['name'] ?? 'this user';
    const firstName: string = userName.split(' ')[0];

    this.modalData = {
      icon: 'unlock',
      title: `Activate ${userName}?`,
      description: `Access will be restored immediately. The user will be able to log in and perform actions.`,
      effects: [
        { type: 'positive', text: 'Login enabled — user can log in immediately' },
        { type: 'positive', text: 'User will appear as an active assignee' }
      ],
      confirmation: {
        label: userName,
        expectedValue: userName,
        placeholder: `Type "${userName}" to confirm`,
      },
      actions: [
        {
          label: 'Activate',
          style: 'primary',
          action: 'activate',
          disabledUntilConfirmed: true,
        },
      ],
    };
    this.isModalOpen = true;
  }

  onModalAction(action: string): void {
    if (action === 'deactivate' && this._pendingActionRow) {
      console.log('Deactivating user:', this._pendingActionRow);

      this.usersService.deactivate(this._pendingActionRow.id)
        .subscribe({
          next: () => {
            // this.loadUsers();
          },
          error: (err) => {
            console.error('Failed to deactivate user:', err);
          },
          complete: () => {
            this._pendingActionRow!['status'] = 'Inactive';
            this.closeModal();

          }
        });
    } else if (action === 'activate' && this._pendingActionRow) {
      console.log('Activating user:', this._pendingActionRow);
      this.usersService.activate(this._pendingActionRow.id)
        .subscribe({
          next: () => {
            // this.loadUsers();
          },
          error: (err) => {
            console.error('Failed to activate user:', err);
          },
          complete: () => {
            this._pendingActionRow!['status'] = 'Active';
            this.closeModal();
          }
        });
    }
  }

  onModalDismiss(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.isModalOpen = false;
    this.modalData = null;
    this._pendingActionRow = null;
  }
}